import { FormControl, FormHelperText, FormLabel, Grid, Theme, ToggleButton, ToggleButtonGroup } from '@mui/material'
import type { ReactNode } from 'react'
import { useController } from 'react-hook-form'

import { HelpButton } from '../components/Help/HelpButton'

type Props = {
  name: string
  label: string | React.ReactNode
  helperText?: React.ReactNode | string
  readOnly?: boolean
  required?: boolean
  moreInfoTitle?: string
  moreInfoText?: string | React.ReactNode
}

export const YesNoInput = ({
  label,
  name,
  helperText,
  readOnly = false,
  required = false,
  moreInfoText,
  moreInfoTitle,
}: Props) => {
  const { field, fieldState } = useController({ name })

  const YesNoInputColor = (theme: Theme) => ({
    '&.Mui-selected': {
      backgroundColor: theme.palette.primary.main,
      color: '#fff',
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
        color: '#FFF',
      },
    },
  })

  const onChangeValue = (value: boolean) => () => {
    if (field.value === value) field.onChange(null)
    else field.onChange(value)

    field.onBlur()
  }

  const extractText = (node: ReactNode): string => {
    if (typeof node === 'string') return node
    if (Array.isArray(node)) return node.map(extractText).join('')
    if (node && typeof node === 'object' && 'props' in node) {
      const props = (node as { props?: { children?: ReactNode } }).props
      if (props?.children) return extractText(props.children)
    }
    return ''
  }

  return (
    <FormControl error={!!fieldState.error} disabled={readOnly} required={required} fullWidth>
      <Grid container spacing={2} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Grid size="auto">
          <ToggleButtonGroup
            aria-label={`Yes/No options for ${extractText(label)}${required ? '. A yes or no answer is required for this field' : '.'}`}
            color={fieldState.error ? 'error' : 'secondary'}
            disabled={readOnly}
          >
            <ToggleButton
              size="small"
              color="secondary"
              value="yes"
              aria-label="Yes"
              selected={field.value === true}
              onClick={onChangeValue(true)}
              sx={YesNoInputColor}
            >
              Yes
            </ToggleButton>
            <ToggleButton
              size="small"
              color="secondary"
              value="no"
              aria-label="No"
              selected={field.value === false}
              onClick={onChangeValue(false)}
              sx={YesNoInputColor}
            >
              No
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid>
        <Grid sx={{ flex: 1 }}>
          <FormLabel>{label}</FormLabel>
        </Grid>
        {moreInfoTitle && moreInfoText && (
          <Grid size="auto">
            <HelpButton title={moreInfoTitle} text={moreInfoText} />
          </Grid>
        )}
      </Grid>
      <FormHelperText error={!!fieldState.error} sx={{ ml: 0 }}>
        {fieldState.error ? fieldState.error.message : helperText}
      </FormHelperText>
    </FormControl>
  )
}
